import { FC, SyntheticEvent, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { IMAGES } from '@image/images';
import { useAppDispatch, useAppSelector } from '@src/store/hooks';
import {
  encrypt,
  formatTimeDifference,
  redirectToExternalUrl,
} from '@src/utils';
import { setIsShowRide } from '@src/store/slices/rides/ridesSlice';
import { updateXHeaderId } from '@src/utils/storage';
import { useLazyGetNotificationByIDQuery } from '@src/store/rtk/twilio';
import SubmitButton from '@src/uikit/FormItems/SubmitButton/SubmitButton';
import {
  handleReading,
  NotificationType,
} from '@store/slices/notification/notification';
import { notificationConfigurations, toastError } from '@utils/helpers';
import { logger } from '@utils/logger';
import { AppleBoldIcon, GoogleLoginIcon, NoDriverShowIcon } from '@src/svg';
import { useReadUserNotificationMutation } from '@store/rtk/users';
import { selectUsers } from '@store/slices/users/usersSlice';
import { postNotificationSync } from '@utils/notificationSync';
import { toastMessage } from '@utils/toastMessage';
import { CONTRACTOR_USER } from '@routes/helpers';

interface NotificationItemProps {
  notification: NotificationType;
  onClose?: () => void;
  refetch?: () => void;
}

const NotificationItem: FC<NotificationItemProps> = ({
  notification,
  onClose,
  refetch,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { userInfo, userType } = useAppSelector(selectUsers);
  const [getNotificationsByID] = useLazyGetNotificationByIDQuery();
  const [readUserNotification] = useReadUserNotificationMutation();
  const pathPrefix = userType === CONTRACTOR_USER ? '' : '/profile';
  const handleRead = async () => {
    if (notification?.isRead) return;

    dispatch(handleReading({ id: notification?.id }));

    try {
      await getNotificationsByID(notification?.id).unwrap();
      postNotificationSync({
        type: 'read',
        id: notification?.id,
        userId: userInfo?.id,
      });
      refetch?.();
    } catch (error) {
      toastError(
        error,
        toastMessage.NOTIFICATION.failedToMarkNotificationAsRead
      );
      logger.error('Failed to mark as read', error);
    }
  };

  const handleRedirectRide = useCallback(async () => {
    if (notification?.rideId) {
      dispatch(setIsShowRide(true));
      updateXHeaderId('ride', notification?.rideId as number);
      window.open(
        `/rides/information/${encrypt(notification?.rideId)}`,
        '_blank'
      );
    } else {
      try {
        readUserNotification({ id: notification?.id });
        onClose && onClose();
        navigate(`${pathPrefix}/user-profile`);
        await handleRead();
      } catch (err) {
        logger.log(err);
      }
    }
  }, [notification?.rideId, userType]);

  const handleRedirectTrack = useCallback(() => {
    if (notification?.rideSupportUrl) {
      redirectToExternalUrl(notification?.rideSupportUrl, true);
    }
  }, [notification?.rideSupportUrl]);

  const rawImage = notificationConfigurations(
    notification.vehiclesImage as string
  )?.[notification?.statusId as number]?.image;

  const imageSrc = rawImage?.includes('whitebg')
    ? rawImage.replace('whitebg', 'transparent')
    : rawImage;

  const handleImageError = (event: SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    if (rawImage && imageSrc !== rawImage && img.dataset.retried !== 'true') {
      img.dataset.retried = 'true';
      img.src = rawImage;
      return;
    }
    if (!img.src.endsWith(IMAGES.carPlaceholder)) {
      img.src = IMAGES.carPlaceholder;
    }
  };

  const isNoDriver = notification?.statusId === 20;

  return (
    <div
      data-test-id="c-notification-item"
      className={`notification-item ${!notification?.isRead ? 'unread' : ''}`}
      onClick={() => {
        !notification.rideId ? {} : handleRead();
      }}
    >
      <div
        data-test-id="c-notification-item-image-block"
        className={`notification-item-image-block ${isNoDriver ? 'no-driver' : ''} ${!notification.rideId ? 'sso_background_dark' : ''}`}
      >
        {!isNoDriver ? (
          <>
            {!notification?.rideId ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#fff',
                }}
              >
                <AppleBoldIcon width={35} height={35} />
                <GoogleLoginIcon width={35} height={35} />
              </div>
            ) : (
              <img
                src={imageSrc || IMAGES.carPlaceholder}
                alt="notification image"
                onError={handleImageError}
              />
            )}
          </>
        ) : (
          <NoDriverShowIcon />
        )}
      </div>

      <div
        className="notification-item-body"
        data-test-id="c-notification-item-body"
      >
        <h5>
          {!notification.rideId ? (
            'Go to Account Settings to setup SSO Login'
          ) : (
            <>
              {
                notificationConfigurations()?.[notification?.statusId as number]
                  ?.title
              }
              {notification?.memberName}
            </>
          )}
        </h5>

        <div
          className="notification-item-content"
          data-test-id="c-notification-item-content"
        >
          <div
            className="notification-item-buttons"
            data-test-id="c-notification-item-buttons"
          >
            <SubmitButton
              dataTestId="c-notification-item-ride-detail-button"
              title={`${notification.rideId ? 'Ride Detail' : 'Account Settings'}`}
              onClick={handleRedirectRide}
              className={`notification-item-button ${notificationConfigurations()?.[notification?.statusId as number]?.isButtonHidden ? 'no_driver' : ''}`}
            />
            {notification?.rideSupportUrl && (
              <SubmitButton
                dataTestId="c-notification-item-track-ride-button"
                title="Track Ride"
                className="notification-item-button"
                onClick={handleRedirectTrack}
              />
            )}
          </div>
          <span
            className="notification-time-ago"
            data-test-id="c-notification-item-time-ago"
          >
            {formatTimeDifference(notification?.createdAt)}
          </span>
        </div>
      </div>
      {!notification?.isRead && <span className="read-mark" />}
    </div>
  );
};

export default NotificationItem;
